import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { formatDistanceToNow, isValid, parse } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import {
    AlertCircle,
    ArrowLeft,
    Bot,
    Check,
    ChevronDown,
    ChevronRight,
    ClipboardCopy,
    Loader2,
    MessageSquare,
    Plus,
    RotateCcw,
    ThumbsDown,
    ThumbsUp,
    User,
    Zap,
} from 'lucide-react';
import { Fragment, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';

import { ListNavControls } from '@apps/web/components/admin/list_nav_controls';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Textarea } from '@apps/web/components/ui/textarea';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import {
    smsDetailSearchToListSearch,
    smsEngineRunsDetailSearchSchema,
} from '@apps/web/routes/_app/admin/sms-engine-runs/search_params';
import { SmsEngineDinoGame } from '@apps/web/routes/_app/admin/sms-engine-runs/sms_engine_dino_game';
import { useSmsRunListNavigation } from '@apps/web/routes/_app/admin/sms-engine-runs/use_sms_run_list_navigation';
import { useSmsEngineRun } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type InlineReviewIssueBucket,
    type SmsEngineRunAnnotationRead,
} from '@libraries/typescript/api_client/schema_types';

const MODEL_OPTIONS = [
    {
        group: 'OpenAI',
        models: [
            'gpt-5.2',
            'gpt-5',
            'gpt-5.6-luna',
            'gpt-4.1',
            'gpt-4o',
            'o3',
            'o3-mini',
            'o4-mini',
        ],
    },
    {
        group: 'Anthropic',
        models: [
            'claude-opus-4-5',
            'claude-opus-4-0',
            'claude-3-7-sonnet-latest',
            'claude-haiku-4-5-20251001',
            'claude-3-5-haiku-latest',
        ],
    },
    {
        group: 'Gemini',
        models: ['gemini-3.6-flash'],
    },
] as const;

const SNAPSHOT_TIMESTAMP_FORMAT = "EEEE, MMMM d, yyyy 'at' h:mm a";

type ReplayResult = {
    readonly model: string;
    readonly raw_reply: string;
    readonly final_reply: string;
    readonly should_auto_send: boolean;
    readonly skip_response: boolean;
    readonly actions: Array<Record<string, unknown>>;
    readonly detected_language: string | null;
    readonly llm_items: Array<LLMItem>;
};

type SmsEngineRunPageCopyAnnotation = {
    readonly id?: string;
    readonly source_type?: string;
    readonly source_name?: string | null;
    readonly created_at: string;
    readonly comment?: string | null;
    readonly annotation_metadata?: unknown;
};

type SmsEngineRunPageCopyInput = {
    readonly id: string;
    readonly created_at: string;
    readonly organization_name?: string | null;
    readonly organization_timezone?: string | null;
    readonly conversation_id: string;
    readonly message_id: string;
    readonly caregiver_id?: string | null;
    readonly caregiver_name?: string | null;
    readonly context_snapshot: unknown;
    readonly model?: string | null;
    readonly llm_items?: Array<LLMItem> | null;
    readonly actions?: Array<Record<string, unknown>> | null;
    readonly raw_reply?: string | null;
    readonly final_reply?: string | null;
    readonly should_auto_send?: boolean | null;
    readonly skip_response?: boolean | null;
    readonly domain_type?: string | null;
    readonly domain_id?: string | null;
    readonly used_override?: boolean | null;
    readonly is_off_hours?: boolean | null;
    readonly detected_language?: string | null;
    readonly braintrust_run_url?: string | null;
    readonly braintrust_run_id?: string | null;
    readonly status?: string | null;
    readonly status_source_type?: string | null;
    readonly status_set_by_user_id?: string | null;
    readonly status_set_by_user_name?: string | null;
    readonly status_set_at?: string | null;
    readonly annotations?: Array<SmsEngineRunPageCopyAnnotation> | null;
};

export const Route = createFileRoute('/_app/admin/sms-engine-runs/$runId')({
    head: () => ({
        meta: [
            {
                title: 'SMS Engine Run | Phoebe Admin',
            },
        ],
    }),
    validateSearch: smsEngineRunsDetailSearchSchema,
    component: SmsEngineRunDetailPage,
});

export function formatSmsEngineSnapshotTimestamp(
    value: string | null | undefined,
    timezone = 'UTC',
) {
    if (!value) {
        return null;
    }

    const isoDate = new Date(value);
    if (!Number.isNaN(isoDate.getTime())) {
        return formatInTimeZone(isoDate, timezone, 'MMM d, yyyy, h:mm a zzz');
    }

    const parsedSnapshotDate = parse(
        value,
        SNAPSHOT_TIMESTAMP_FORMAT,
        new Date(),
    );
    if (isValid(parsedSnapshotDate)) {
        return formatInTimeZone(
            fromZonedTime(parsedSnapshotDate, timezone),
            timezone,
            'MMM d, yyyy, h:mm a zzz',
        );
    }

    return value;
}

function SmsEngineRunDetailPage() {
    const router = useRouter();
    const { runId } = Route.useParams();
    const returnSearch = Route.useSearch();
    const listNav = useSmsRunListNavigation(runId, returnSearch);
    const { data: run, isLoading, error, mutate } = useSmsEngineRun(runId);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [newCommentText, setNewCommentText] = useState('');
    const [isAddingComment, setIsAddingComment] = useState(false);
    const [replayModel, setReplayModel] = useState('');
    const [isReplaying, setIsReplaying] = useState(false);
    const [replayResult, setReplayResult] = useState<ReplayResult | null>(null);
    const [replayError, setReplayError] = useState<string | null>(null);
    const [pageCopied, setPageCopied] = useState(false);

    const handleStatusUpdate = async (
        status: 'unreviewed' | 'needs_review' | 'good' | 'bad',
    ) => {
        if (!run) {
            return;
        }
        const nextStatus =
            run.status === status && status !== 'unreviewed'
                ? 'unreviewed'
                : status;
        setIsUpdatingStatus(true);
        try {
            const client = getApiClient();
            await client.put({
                url: '/admin/sms-engine-runs/{run_id}/status',
                pathParams: { run_id: runId },
                body: { status: nextStatus },
            });
            await mutate();
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleAddComment = async () => {
        if (!run || !newCommentText.trim()) {
            return;
        }
        setIsAddingComment(true);
        try {
            const client = getApiClient();
            await client.post({
                url: '/admin/sms-engine-runs/{run_id}/annotations',
                pathParams: { run_id: runId },
                body: {
                    source_type: 'human',
                    comment: newCommentText.trim(),
                },
            });
            setNewCommentText('');
            await mutate();
        } finally {
            setIsAddingComment(false);
        }
    };

    const handleCopyPage = async () => {
        if (!run) {
            return;
        }
        try {
            await navigator.clipboard.writeText(
                buildSmsEngineRunPageCopyText({ run, replayResult }),
            );
            setPageCopied(true);
            setTimeout(() => setPageCopied(false), 2000);
            toast.success('Page copied to clipboard', {
                persistNotification: false,
            });
        } catch {
            toast.error('Unable to copy page', {
                persistNotification: false,
            });
        }
    };

    const handleReplay = async () => {
        if (!replayModel) {
            return;
        }
        setIsReplaying(true);
        setReplayError(null);
        try {
            const client = getApiClient();
            const response = await client.post({
                url: '/admin/sms-engine-runs/{run_id}/replay',
                pathParams: { run_id: runId },
                body: { model: replayModel },
            });
            setReplayResult(response as ReplayResult);
        } catch (err) {
            setReplayError(
                err instanceof Error ? err.message : 'Replay failed',
            );
            setReplayResult(null);
        } finally {
            setIsReplaying(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <SmsEngineDinoGame className="max-w-[380px]" />
            </div>
        );
    }

    if (error || !run) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-red-500">
                    {error?.message ?? 'SMS engine run not found'}
                </div>
            </div>
        );
    }

    const snapshot = run.context_snapshot as {
        caregiver?: {
            id?: string;
            first_name?: string;
            last_name?: string;
            language?: string;
        };
        messages?: Array<{
            id?: string;
            role?: string;
            body?: string;
            source?:
                | 'ai'
                | 'automated'
                | 'human'
                | 'external'
                | 'ringcentral'
                | null;
            timestamp?: string | null;
            domain_links?: Array<{
                type?: string;
                shift_confirmation_id?: string;
                contact_attempt_id?: string;
                callout_id?: string;
                shift_date?: string;
                shift_start_time?: string;
                shift_end_time?: string;
                patient_address?: string;
            }>;
        }>;
        inbound?: {
            body?: string;
            received_at?: string | null;
        };
        active_shifts?: Array<{
            shift_id?: string;
            shift_date?: string;
            shift_start_time?: string;
            shift_end_time?: string;
        }>;
    };
    const cardClassName = 'shadow-none py-4 gap-4';
    const cardHeaderClassName = 'pb-2';

    const statusSetBy =
        run.status_source_type === 'agent'
            ? 'Agent'
            : run.status_set_by_user_name ||
              (run.status_source_type === 'user'
                  ? 'User Feedback'
                  : run.status_source_type === 'human'
                    ? 'Human'
                    : null);
    const formatTimestamp = (value: string | null) =>
        value
            ? formatInTimeZone(
                  new Date(value),
                  run.organization_timezone || 'UTC',
                  'PPpp',
              )
            : '—';
    const formatConversationTimestamp = (value: string | null | undefined) =>
        formatSmsEngineSnapshotTimestamp(
            value,
            run.organization_timezone || 'UTC',
        );
    const inboundTimestamp = formatConversationTimestamp(
        snapshot.inbound?.received_at,
    );
    const generatedReplyTimestamp = formatTimestamp(run.created_at);
    const agencyEscalations = extractAgencyEscalationActions(run.actions);

    return (
        <div className="flex h-full flex-col">
            {/* Header */}
            <div className="border-border flex items-center justify-between border-b p-4">
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Back to SMS engine runs"
                        onClick={() =>
                            router.navigate({
                                to: '/admin/sms-engine-runs',
                                search: smsDetailSearchToListSearch(
                                    returnSearch,
                                ),
                            })
                        }
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <ListNavControls navigation={listNav} />
                    <div>
                        <h1 className="text-lg font-semibold">{run.id}</h1>
                        <p className="text-muted-foreground text-sm">
                            {formatDistanceToNow(new Date(run.created_at), {
                                addSuffix: true,
                            })}{' '}
                            (
                            {formatInTimeZone(
                                new Date(run.created_at),
                                run.organization_timezone || 'UTC',
                                'PPpp',
                            )}
                            )
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <RunStatusBadge status={run.status} />
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyPage}
                    >
                        {pageCopied ? (
                            <Check className="mr-1.5 h-4 w-4" />
                        ) : (
                            <ClipboardCopy className="mr-1.5 h-4 w-4" />
                        )}
                        {pageCopied ? 'Copied' : 'Copy page'}
                    </Button>
                    <Link
                        to="/admin/sms-debug"
                        className="hover:bg-muted/50 inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm"
                    >
                        <MessageSquare className="h-4 w-4" />
                        Debug
                    </Link>
                </div>
            </div>
            <ScrollArea className="flex-1">
                <div className="flex w-full max-w-full min-w-0 flex-col gap-6 p-6 lg:flex-row lg:gap-0">
                    <div className="flex w-full min-w-0 flex-col gap-6 lg:w-0 lg:flex-[2] lg:pr-6">
                        {/* Context Section */}
                        <Card className={cardClassName}>
                            <CardHeader className={cardHeaderClassName}>
                                <CardTitle className="text-base">
                                    Context
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                    <div className="space-y-1">
                                        <p className="text-muted-foreground text-xs">
                                            Organization
                                        </p>
                                        <p className="text-sm font-medium">
                                            {run.organization_name || '—'}
                                        </p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-muted-foreground text-xs">
                                            Caregiver
                                        </p>
                                        <p className="text-sm font-medium">
                                            {run.caregiver_name || '—'}
                                            {snapshot.caregiver?.language && (
                                                <span className="text-muted-foreground ml-1">
                                                    (
                                                    {
                                                        snapshot.caregiver
                                                            .language
                                                    }
                                                    )
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-muted-foreground text-xs">
                                            Model
                                        </p>
                                        <code className="bg-muted rounded px-1 text-sm">
                                            {run.model || '—'}
                                        </code>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-muted-foreground text-xs">
                                            Domain
                                        </p>
                                        <p className="text-sm font-medium">
                                            {run.domain_type || '—'}
                                        </p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-muted-foreground text-xs">
                                            Conversation ID
                                        </p>
                                        <Link
                                            to="/conversations"
                                            search={{
                                                conversationId:
                                                    run.conversation_id,
                                            }}
                                            className="inline-flex min-w-0 items-center"
                                        >
                                            <code className="bg-muted rounded px-1 text-sm break-all underline-offset-2 hover:underline">
                                                {run.conversation_id}
                                            </code>
                                        </Link>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-muted-foreground text-xs">
                                            Braintrust Run
                                        </p>
                                        {run.braintrust_run_url ? (
                                            <a
                                                href={run.braintrust_run_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-sm font-medium underline underline-offset-2"
                                            >
                                                Open in Braintrust
                                            </a>
                                        ) : run.braintrust_run_id ? (
                                            <code className="bg-muted rounded px-1 text-sm">
                                                {run.braintrust_run_id}
                                            </code>
                                        ) : (
                                            <p className="text-muted-foreground text-sm">
                                                —
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        {/* Conversation */}
                        <Card className={cardClassName}>
                            <CardHeader className={cardHeaderClassName}>
                                <CardTitle className="text-base">
                                    Conversation
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <p className="text-muted-foreground mb-2 text-xs">
                                        Context (engine input)
                                    </p>
                                    {snapshot.messages &&
                                    snapshot.messages.length > 0 ? (
                                        <div className="space-y-3">
                                            {snapshot.messages.map(
                                                (msg, idx) => {
                                                    const messageTimestamp =
                                                        formatConversationTimestamp(
                                                            msg.timestamp,
                                                        );
                                                    return (
                                                        <div
                                                            key={idx}
                                                            className={cn(
                                                                'rounded-lg p-3',
                                                                msg.role ===
                                                                    'user'
                                                                    ? 'bg-blue-50 dark:bg-blue-950/50'
                                                                    : 'bg-muted/50',
                                                            )}
                                                        >
                                                            <div className="mb-1 flex flex-wrap items-center gap-2">
                                                                <Badge
                                                                    variant="outline"
                                                                    className="text-xs"
                                                                >
                                                                    {msg.role ===
                                                                    'user'
                                                                        ? 'Caregiver'
                                                                        : 'Phoebe'}
                                                                </Badge>
                                                                {msg.source && (
                                                                    <Badge
                                                                        variant="secondary"
                                                                        className="text-xs"
                                                                    >
                                                                        {
                                                                            msg.source
                                                                        }
                                                                    </Badge>
                                                                )}
                                                                {msg.domain_links &&
                                                                    msg
                                                                        .domain_links
                                                                        .length >
                                                                        0 && (
                                                                        <div className="flex gap-1">
                                                                            {msg.domain_links.map(
                                                                                (
                                                                                    link,
                                                                                    linkIdx,
                                                                                ) => (
                                                                                    <Badge
                                                                                        key={
                                                                                            linkIdx
                                                                                        }
                                                                                        variant="secondary"
                                                                                        className="text-xs"
                                                                                    >
                                                                                        {link.type ===
                                                                                        'shift_confirmation'
                                                                                            ? 'Shift Confirmation'
                                                                                            : link.type ===
                                                                                                'callout_contact_attempt'
                                                                                              ? 'Open Shift'
                                                                                              : link.type}
                                                                                    </Badge>
                                                                                ),
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                {messageTimestamp && (
                                                                    <span
                                                                        className="text-muted-foreground ml-auto text-right text-xs"
                                                                        title={
                                                                            msg.timestamp ??
                                                                            undefined
                                                                        }
                                                                    >
                                                                        {
                                                                            messageTimestamp
                                                                        }
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm break-words whitespace-pre-wrap">
                                                                {msg.body}
                                                            </p>
                                                        </div>
                                                    );
                                                },
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-muted-foreground text-sm">
                                            No context messages available
                                        </p>
                                    )}
                                </div>
                                {snapshot.inbound?.body && (
                                    <div>
                                        <p className="text-muted-foreground mb-2 text-xs">
                                            Inbound message (triggered this run)
                                        </p>
                                        <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-950/50">
                                            <div className="mb-1 flex flex-wrap items-center gap-2">
                                                <Badge
                                                    variant="outline"
                                                    className="text-xs"
                                                >
                                                    Caregiver
                                                </Badge>
                                                {inboundTimestamp && (
                                                    <span
                                                        className="text-muted-foreground ml-auto text-right text-xs"
                                                        title={
                                                            snapshot.inbound
                                                                .received_at ??
                                                            undefined
                                                        }
                                                    >
                                                        {inboundTimestamp}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm break-words whitespace-pre-wrap">
                                                {snapshot.inbound.body}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <div className="flex items-center gap-3 py-2">
                                    <div className="bg-border h-px flex-1" />
                                    <span className="text-muted-foreground text-xs tracking-wide uppercase">
                                        Generated Reply
                                    </span>
                                    <div className="bg-border h-px flex-1" />
                                </div>
                                <div className="rounded-lg bg-green-50 p-3 dark:bg-green-950/50">
                                    <div className="mb-1 flex flex-wrap items-center gap-2">
                                        <Badge
                                            variant="outline"
                                            className="text-xs"
                                        >
                                            Phoebe
                                        </Badge>
                                        <Badge
                                            variant="secondary"
                                            className="text-xs"
                                        >
                                            ai
                                        </Badge>
                                        <span
                                            className="text-muted-foreground ml-auto text-right text-xs"
                                            title="SMS engine run created at"
                                        >
                                            {generatedReplyTimestamp}
                                        </span>
                                    </div>
                                    <p className="text-sm whitespace-pre-wrap">
                                        {run.final_reply || '(empty)'}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-3 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">
                                            Auto-send:
                                        </span>{' '}
                                        {run.should_auto_send ? 'Yes' : 'No'}
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">
                                            Skip Response:
                                        </span>{' '}
                                        {run.skip_response ? 'Yes' : 'No'}
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">
                                            Override Used:
                                        </span>{' '}
                                        {run.used_override ? 'Yes' : 'No'}
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">
                                            Off Hours:
                                        </span>{' '}
                                        {run.is_off_hours ? 'Yes' : 'No'}
                                    </div>
                                    {run.detected_language && (
                                        <div>
                                            <span className="text-muted-foreground">
                                                Detected Language:
                                            </span>{' '}
                                            {run.detected_language}
                                        </div>
                                    )}
                                </div>
                                <Collapsible>
                                    <CollapsibleTrigger className="text-muted-foreground flex items-center gap-1 text-xs">
                                        <ChevronRight className="h-3.5 w-3.5" />
                                        Raw LLM Reply
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="bg-muted/50 mt-2 rounded-lg p-3 text-sm break-words whitespace-pre-wrap">
                                            {run.raw_reply || '(empty)'}
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                            </CardContent>
                        </Card>
                        {/* Replay */}
                        <Card
                            className={cn(
                                cardClassName,
                                'border-2 border-purple-300 dark:border-purple-800',
                            )}
                        >
                            <CardHeader className={cardHeaderClassName}>
                                <CardTitle className="text-base">
                                    Replay
                                    {replayResult && (
                                        <code className="ml-1 rounded bg-purple-100 px-1.5 py-0.5 text-xs font-normal text-purple-700 dark:bg-purple-950/50 dark:text-purple-300">
                                            {replayResult.model}
                                        </code>
                                    )}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {replayResult ? (
                                    <Fragment>
                                        <div className="rounded-lg bg-purple-50 p-3 dark:bg-purple-950/50">
                                            <div className="mb-1 flex items-center gap-2">
                                                <Badge
                                                    variant="outline"
                                                    className="text-xs"
                                                >
                                                    Phoebe (replay)
                                                </Badge>
                                            </div>
                                            <p className="text-sm whitespace-pre-wrap">
                                                {replayResult.final_reply ||
                                                    '(empty)'}
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap gap-3 text-sm">
                                            <div>
                                                <span className="text-muted-foreground">
                                                    Auto-send:
                                                </span>{' '}
                                                {replayResult.should_auto_send
                                                    ? 'Yes'
                                                    : 'No'}
                                            </div>
                                            <div>
                                                <span className="text-muted-foreground">
                                                    Skip Response:
                                                </span>{' '}
                                                {replayResult.skip_response
                                                    ? 'Yes'
                                                    : 'No'}
                                            </div>
                                            {replayResult.detected_language && (
                                                <div>
                                                    <span className="text-muted-foreground">
                                                        Detected Language:
                                                    </span>{' '}
                                                    {
                                                        replayResult.detected_language
                                                    }
                                                </div>
                                            )}
                                        </div>
                                        <Collapsible>
                                            <CollapsibleTrigger className="text-muted-foreground flex items-center gap-1 text-xs">
                                                <ChevronRight className="h-3.5 w-3.5" />
                                                Raw LLM Reply
                                            </CollapsibleTrigger>
                                            <CollapsibleContent>
                                                <div className="bg-muted/50 mt-2 rounded-lg p-3 text-sm break-words whitespace-pre-wrap">
                                                    {replayResult.raw_reply ||
                                                        '(empty)'}
                                                </div>
                                            </CollapsibleContent>
                                        </Collapsible>
                                        {replayResult.actions.length > 0 && (
                                            <div>
                                                <p className="text-muted-foreground mb-2 text-xs">
                                                    Extracted Actions
                                                </p>
                                                <ul className="space-y-2">
                                                    {replayResult.actions.map(
                                                        (action, idx) => (
                                                            <li
                                                                key={idx}
                                                                className="flex items-center gap-2 text-sm"
                                                            >
                                                                <Check className="h-4 w-4 text-purple-500" />
                                                                <span className="font-medium">
                                                                    <ActionLabel
                                                                        action={
                                                                            action
                                                                        }
                                                                    />
                                                                </span>
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                        <Collapsible>
                                            <CollapsibleTrigger className="text-muted-foreground flex items-center gap-1 text-xs">
                                                <ChevronRight className="h-3.5 w-3.5" />
                                                LLM Execution Items (
                                                {replayResult.llm_items.length})
                                            </CollapsibleTrigger>
                                            <CollapsibleContent>
                                                <div className="mt-2 space-y-1">
                                                    {replayResult.llm_items.map(
                                                        (item, idx) => (
                                                            <LLMItemStep
                                                                key={idx}
                                                                item={item}
                                                            />
                                                        ),
                                                    )}
                                                </div>
                                            </CollapsibleContent>
                                        </Collapsible>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <p className="text-muted-foreground text-sm">
                                            Re-run the SMS engine on this
                                            conversation with a different model
                                            to compare outputs.
                                        </p>
                                        {replayError && (
                                            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                                                <p className="font-medium">
                                                    Replay failed
                                                </p>
                                                <p>{replayError}</p>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-3">
                                            <Select
                                                value={replayModel}
                                                onValueChange={setReplayModel}
                                            >
                                                <SelectTrigger
                                                    size="sm"
                                                    className="w-[180px]"
                                                >
                                                    <SelectValue placeholder="Select model..." />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-[min(300px,var(--radix-select-content-available-height))]">
                                                    {MODEL_OPTIONS.map(
                                                        (group) => (
                                                            <SelectGroup
                                                                key={
                                                                    group.group
                                                                }
                                                            >
                                                                <SelectLabel>
                                                                    {
                                                                        group.group
                                                                    }
                                                                </SelectLabel>
                                                                {group.models.map(
                                                                    (model) => (
                                                                        <SelectItem
                                                                            key={
                                                                                model
                                                                            }
                                                                            value={
                                                                                model
                                                                            }
                                                                        >
                                                                            {
                                                                                model
                                                                            }
                                                                        </SelectItem>
                                                                    ),
                                                                )}
                                                            </SelectGroup>
                                                        ),
                                                    )}
                                                </SelectContent>
                                            </Select>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleReplay}
                                                disabled={
                                                    !replayModel || isReplaying
                                                }
                                            >
                                                {isReplaying ? (
                                                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                                                ) : (
                                                    <RotateCcw className="mr-1.5 h-4 w-4" />
                                                )}
                                                Replay
                                            </Button>
                                        </div>
                                        {isReplaying && (
                                            <SmsEngineDinoGame
                                                compact={true}
                                                delayMs={700}
                                                loadingLabel="Replaying SMS engine run…"
                                            />
                                        )}
                                    </Fragment>
                                )}
                            </CardContent>
                        </Card>
                        {/* Pending Items */}
                        {(snapshot.messages?.some(
                            (m) => m.domain_links && m.domain_links.length > 0,
                        ) ||
                            (snapshot.active_shifts &&
                                snapshot.active_shifts.length > 0)) && (
                            <Card className={cardClassName}>
                                <CardHeader className={cardHeaderClassName}>
                                    <CardTitle className="text-base">
                                        Pending Items (what engine saw)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {snapshot.messages?.some(
                                        (m) =>
                                            m.domain_links &&
                                            m.domain_links.length > 0,
                                    ) && (
                                        <div>
                                            <h4 className="mb-2 text-sm font-medium">
                                                Domain Links
                                            </h4>
                                            <ul className="space-y-2 text-sm">
                                                {snapshot.messages?.flatMap(
                                                    (msg) =>
                                                        msg.domain_links?.map(
                                                            (link, idx) => (
                                                                <li
                                                                    key={`${msg.id}-${idx}`}
                                                                    className="flex items-start gap-2"
                                                                >
                                                                    <span className="text-muted-foreground">
                                                                        •
                                                                    </span>
                                                                    <span className="break-words">
                                                                        {link.type ===
                                                                        'shift_confirmation'
                                                                            ? `Shift Confirmation: ${link.shift_date} ${link.shift_start_time}-${link.shift_end_time}`
                                                                            : link.type ===
                                                                                'callout_contact_attempt'
                                                                              ? `Open Shift Offer: ${link.shift_date} ${link.shift_start_time}-${link.shift_end_time}`
                                                                              : link.type}
                                                                        {link.patient_address && (
                                                                            <span className="text-muted-foreground ml-1">
                                                                                at{' '}
                                                                                {
                                                                                    link.patient_address
                                                                                }
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </li>
                                                            ),
                                                        ) ?? [],
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                    {snapshot.active_shifts &&
                                        snapshot.active_shifts.length > 0 && (
                                            <div>
                                                <h4 className="mb-2 text-sm font-medium">
                                                    Active Shifts
                                                </h4>
                                                <ul className="space-y-2 text-sm">
                                                    {snapshot.active_shifts.map(
                                                        (shift, idx) => (
                                                            <li
                                                                key={`shift-${idx}`}
                                                                className="flex items-start gap-2"
                                                            >
                                                                <span className="text-muted-foreground">
                                                                    •
                                                                </span>
                                                                <span>
                                                                    {
                                                                        shift.shift_date
                                                                    }{' '}
                                                                    {
                                                                        shift.shift_start_time
                                                                    }
                                                                    -
                                                                    {
                                                                        shift.shift_end_time
                                                                    }
                                                                </span>
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                </CardContent>
                            </Card>
                        )}
                        {/* LLM Execution */}
                        <Card className={cardClassName}>
                            <CardHeader className={cardHeaderClassName}>
                                <CardTitle className="text-base">
                                    LLM Execution
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-1">
                                    {run.llm_items?.map((item, idx) => (
                                        <LLMItemStep key={idx} item={item} />
                                    ))}
                                    {(!run.llm_items ||
                                        run.llm_items.length === 0) && (
                                        <p className="text-muted-foreground text-sm">
                                            No LLM items recorded
                                        </p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                        {/* Extracted Actions */}
                        <Card className={cardClassName}>
                            <CardHeader className={cardHeaderClassName}>
                                <CardTitle className="text-base">
                                    Extracted Actions
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {run.actions && run.actions.length > 0 ? (
                                    <ul className="space-y-2">
                                        {run.actions.map((action, idx) => (
                                            <li
                                                key={idx}
                                                className="flex items-center gap-2 text-sm"
                                            >
                                                <Check className="h-4 w-4 text-green-500" />
                                                <span className="font-medium">
                                                    <ActionLabel
                                                        action={action}
                                                    />
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-muted-foreground text-sm">
                                        No actions extracted
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                        {agencyEscalations.length > 0 && (
                            <Card className={cardClassName}>
                                <CardHeader className={cardHeaderClassName}>
                                    <CardTitle className="text-base">
                                        Agency Escalation
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <AgencyEscalationList
                                        escalations={agencyEscalations}
                                    />
                                </CardContent>
                            </Card>
                        )}
                    </div>
                    <div className="lg:border-border w-full min-w-0 lg:-mt-6 lg:w-0 lg:flex-[1] lg:border-l lg:pt-6 lg:pl-6">
                        <div className="space-y-6">
                            {/* Status Section */}
                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold">
                                    Status
                                </h3>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <RunStatusBadge status={run.status} />
                                        {statusSetBy && (
                                            <span className="text-muted-foreground text-sm">
                                                set by {statusSetBy}
                                            </span>
                                        )}
                                        {run.status_source_type && (
                                            <Badge
                                                variant="secondary"
                                                className="text-xs capitalize"
                                            >
                                                {run.status_source_type}
                                            </Badge>
                                        )}
                                    </div>
                                    {run.status_set_at && (
                                        <span className="text-muted-foreground text-xs">
                                            {formatDistanceToNow(
                                                new Date(run.status_set_at),
                                                { addSuffix: true },
                                            )}
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            handleStatusUpdate('good')
                                        }
                                        disabled={isUpdatingStatus}
                                    >
                                        <ThumbsUp className="mr-1 h-4 w-4" />
                                        Mark Good
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            handleStatusUpdate('bad')
                                        }
                                        disabled={isUpdatingStatus}
                                    >
                                        <ThumbsDown className="mr-1 h-4 w-4" />
                                        Mark Bad
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            handleStatusUpdate('needs_review')
                                        }
                                        disabled={isUpdatingStatus}
                                    >
                                        <AlertCircle className="mr-1 h-4 w-4" />
                                        Needs Review
                                    </Button>
                                </div>
                            </section>
                            {/* Comments Section */}
                            <section className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-semibold">
                                        Comments
                                    </h3>
                                    {run.annotations.length > 0 && (
                                        <Badge
                                            variant="secondary"
                                            className="text-xs"
                                        >
                                            {run.annotations.length} Comment
                                            {run.annotations.length !== 1
                                                ? 's'
                                                : ''}
                                        </Badge>
                                    )}
                                </div>
                                <div className="space-y-3">
                                    <div className="flex gap-2">
                                        <Textarea
                                            placeholder="Add a comment..."
                                            value={newCommentText}
                                            onChange={(e) =>
                                                setNewCommentText(
                                                    e.target.value,
                                                )
                                            }
                                            className="min-h-[60px]"
                                        />
                                        <Button
                                            size="sm"
                                            onClick={handleAddComment}
                                            disabled={
                                                isAddingComment ||
                                                !newCommentText.trim()
                                            }
                                        >
                                            {isAddingComment ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Plus className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                    <AnnotationList
                                        annotations={run.annotations}
                                        organizationId={run.organization_id}
                                        organizationName={
                                            run.organization_name ?? ''
                                        }
                                        runFinalReply={run.final_reply}
                                        runRawReply={run.raw_reply}
                                        emptyMessage="No comments on this run"
                                    />
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </ScrollArea>
        </div>
    );
}

function RunStatusBadge({
    status,
}: {
    status: 'unreviewed' | 'needs_review' | 'good' | 'bad';
}) {
    if (status === 'good') {
        return (
            <Badge className="bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300">
                <ThumbsUp className="mr-1 h-3 w-3" />
                Good
            </Badge>
        );
    }

    if (status === 'unreviewed') {
        return (
            <Badge variant="outline" className="text-muted-foreground">
                Unreviewed
            </Badge>
        );
    }

    if (status === 'bad') {
        return (
            <Badge className="bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300">
                <ThumbsDown className="mr-1 h-3 w-3" />
                Bad
            </Badge>
        );
    }

    return (
        <Badge variant="outline" className="text-muted-foreground">
            <AlertCircle className="mr-1 h-3 w-3" />
            Needs review
        </Badge>
    );
}

function AnnotationList({
    annotations,
    emptyMessage,
    organizationId,
    organizationName,
    runFinalReply,
    runRawReply,
}: {
    annotations: Array<SmsEngineRunAnnotationRead>;
    emptyMessage: string;
    organizationId: string;
    organizationName: string;
    runFinalReply?: string | null;
    runRawReply?: string | null;
}) {
    if (annotations.length === 0) {
        return (
            <p className="text-muted-foreground py-4 text-center text-sm">
                {emptyMessage}
            </p>
        );
    }

    return (
        <div className="space-y-3">
            {annotations.map((annotation) => (
                <AnnotationItem
                    key={annotation.id}
                    annotation={annotation}
                    organizationId={organizationId}
                    organizationName={organizationName}
                    runFinalReply={runFinalReply}
                    runRawReply={runRawReply}
                />
            ))}
        </div>
    );
}

const compactMarkdownComponents: Components = {
    h1: ({ ...props }) => <strong className="mt-3 block text-sm" {...props} />,
    h2: ({ ...props }) => <strong className="mt-3 block text-sm" {...props} />,
    h3: ({ ...props }) => <strong className="mt-2 block text-sm" {...props} />,
    p: ({ ...props }) => <span className="my-1 block" {...props} />,
    strong: ({ ...props }) => <strong className="font-semibold" {...props} />,
    ul: ({ ...props }) => (
        <ul
            className="mt-2 mb-1.5 ml-4 list-disc space-y-1 text-sm"
            {...props}
        />
    ),
    ol: ({ ...props }) => (
        <ol
            className="mt-2 mb-1.5 ml-4 list-decimal space-y-1 text-sm"
            {...props}
        />
    ),
    li: ({ ...props }) => <li className="text-sm" {...props} />,
    pre: ({ className, ...props }) => (
        <pre
            className={cn(
                'bg-muted my-1.5 overflow-x-auto rounded-md p-2 text-xs leading-snug whitespace-pre-wrap',
                className,
            )}
            {...props}
        />
    ),
    code: ({ className, ...props }) => {
        const isCodeBlock = className?.includes('language-') === true;

        return (
            <code
                className={cn(
                    isCodeBlock
                        ? 'block bg-transparent p-0 font-mono text-xs leading-snug whitespace-pre-wrap'
                        : 'bg-muted rounded px-1 text-xs',
                    className,
                )}
                {...props}
            />
        );
    },
};

type AnnotationMetadata = {
    readonly score?: number;
    readonly reasons?: Array<string>;
    readonly agent_version?: string;
    readonly verdict?: string;
    readonly issues?: Array<string>;
    readonly issue_buckets?: Array<InlineReviewIssueBucket | string>;
    readonly model?: string;
    readonly rating_label?: string;
    readonly inline_review_mode?: string;
    readonly original_reply?: string | null;
    readonly changed_reply?: string | null;
    readonly preview_reply?: string | null;
    readonly talent_signal?: unknown;
};

type TalentSignalMetadata = {
    readonly category?: unknown;
    readonly confidence?: unknown;
    readonly reason?: unknown;
    readonly evidence_quote?: unknown;
};

type TalentSignalDisplay = {
    readonly category: string | null;
    readonly confidence: string | null;
    readonly reason: string | null;
    readonly evidenceQuote: string | null;
};

type ReplyComparison = {
    readonly originalReply: string;
    readonly changedReply: string;
};

const INLINE_REVIEW_ISSUE_BUCKET_LABELS: Record<
    InlineReviewIssueBucket,
    string
> = {
    intent_understanding: 'Intent',
    action_selection: 'Action',
    reply_accuracy: 'Reply',
    auto_send_safety: 'Auto-send',
    language_or_tone: 'Language/tone',
    workflow_policy: 'Policy',
    context_gap: 'Context',
    other: 'Other',
};

const TALENT_SIGNAL_CATEGORY_LABELS: Record<string, string> = {
    availability_update: 'Availability update',
    open_shift_request: 'Open shift request',
    other_scheduling_signal: 'Other scheduling signal',
    preference_update: 'Preference update',
};

const TALENT_SIGNAL_CONFIDENCE_LABELS: Record<string, string> = {
    high: 'High',
    low: 'Low',
    medium: 'Medium',
};

function formatInlineReviewIssueBucket(bucket: string) {
    return (
        INLINE_REVIEW_ISSUE_BUCKET_LABELS[bucket as InlineReviewIssueBucket] ??
        bucket
    );
}

function cleanMetadataText(value: unknown) {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmedValue = value.trim();
    return trimmedValue ? trimmedValue : null;
}

function getTalentSignalDisplay(
    metadata: AnnotationMetadata | null,
): TalentSignalDisplay | null {
    const rawSignal = metadata?.talent_signal;
    if (!rawSignal || typeof rawSignal !== 'object') {
        return null;
    }

    const signal = rawSignal as TalentSignalMetadata;
    const display = {
        category: cleanMetadataText(signal.category),
        confidence: cleanMetadataText(signal.confidence),
        reason: cleanMetadataText(signal.reason),
        evidenceQuote: cleanMetadataText(signal.evidence_quote),
    };

    return Object.values(display).some((value) => value !== null)
        ? display
        : null;
}

function formatTalentSignalCategory(value: string) {
    return (
        TALENT_SIGNAL_CATEGORY_LABELS[value] ??
        formatSnakeCaseFallbackLabel(value)
    );
}

function formatTalentSignalConfidence(value: string) {
    return (
        TALENT_SIGNAL_CONFIDENCE_LABELS[value] ??
        formatSnakeCaseFallbackLabel(value)
    );
}

function cleanReply(reply?: string | null) {
    const trimmedReply = reply?.trim();
    return trimmedReply ? trimmedReply : null;
}

function hasReplyComparisonMetadata(metadata: AnnotationMetadata | null) {
    // Inline review metadata comes from a JSON column, where a present null
    // means "review ran and did not change the reply"; missing keys mean a
    // legacy annotation that still needs the raw/final reply fallback.
    return (
        metadata !== null &&
        Object.prototype.hasOwnProperty.call(metadata, 'original_reply') &&
        Object.prototype.hasOwnProperty.call(metadata, 'changed_reply')
    );
}

export function buildInlineReviewReplyComparison({
    metadata,
    runFinalReply,
    runRawReply,
}: {
    metadata: AnnotationMetadata | null;
    runFinalReply?: string | null;
    runRawReply?: string | null;
}): ReplyComparison | null {
    const metadataOriginalReply = cleanReply(metadata?.original_reply);
    const metadataChangedReply = cleanReply(metadata?.changed_reply);
    if (
        metadataOriginalReply &&
        metadataChangedReply &&
        metadataOriginalReply !== metadataChangedReply
    ) {
        return {
            originalReply: metadataOriginalReply,
            changedReply: metadataChangedReply,
        };
    }

    const previewReply = cleanReply(metadata?.preview_reply);
    if (previewReply) {
        const originalReply =
            metadataOriginalReply ??
            cleanReply(runFinalReply) ??
            cleanReply(runRawReply);
        if (originalReply && originalReply !== previewReply) {
            return {
                originalReply,
                changedReply: previewReply,
            };
        }
    }

    if (
        metadata?.inline_review_mode === 'enforce' &&
        !hasReplyComparisonMetadata(metadata)
    ) {
        const originalReply = metadataOriginalReply ?? cleanReply(runRawReply);
        const changedReply = metadataChangedReply ?? cleanReply(runFinalReply);
        if (originalReply && changedReply && originalReply !== changedReply) {
            return {
                originalReply,
                changedReply,
            };
        }
    }

    return null;
}

function InlineReviewReplyComparison({
    comparison,
}: {
    comparison: ReplyComparison;
}) {
    return (
        <div className="mt-2 space-y-2 text-sm">
            <div>
                <div className="mb-1 text-xs font-semibold">Original reply</div>
                <pre className="bg-muted overflow-x-auto rounded-md p-2 text-xs leading-snug whitespace-pre-wrap">
                    {comparison.originalReply}
                </pre>
            </div>
            <div>
                <div className="mb-1 text-xs font-semibold">Changed reply</div>
                <pre className="bg-muted overflow-x-auto rounded-md p-2 text-xs leading-snug whitespace-pre-wrap">
                    {comparison.changedReply}
                </pre>
            </div>
        </div>
    );
}

function TalentSignalDetails({ signal }: { signal: TalentSignalDisplay }) {
    return (
        <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground">Talent signal:</span>
                <Badge
                    variant="secondary"
                    className="bg-amber-100 text-xs text-amber-900 dark:bg-amber-950/50 dark:text-amber-300"
                >
                    {signal.category
                        ? formatTalentSignalCategory(signal.category)
                        : 'Detected'}
                </Badge>
                {signal.confidence ? (
                    <Badge variant="outline" className="text-xs">
                        {formatTalentSignalConfidence(signal.confidence)}{' '}
                        confidence
                    </Badge>
                ) : null}
            </div>
            {signal.reason ? (
                <p className="text-xs leading-snug">
                    <span className="text-muted-foreground">Reason:</span>{' '}
                    {signal.reason}
                </p>
            ) : null}
            {signal.evidenceQuote ? (
                <p className="text-xs leading-snug">
                    <span className="text-muted-foreground">Evidence:</span>{' '}
                    {signal.evidenceQuote}
                </p>
            ) : null}
        </div>
    );
}

function AnnotationItem({
    annotation,
    organizationId,
    organizationName,
    runFinalReply,
    runRawReply,
}: {
    annotation: SmsEngineRunAnnotationRead;
    organizationId: string;
    organizationName: string;
    runFinalReply?: string | null;
    runRawReply?: string | null;
}) {
    const metadata =
        annotation.annotation_metadata as AnnotationMetadata | null;
    const talentSignal = getTalentSignalDisplay(metadata);
    const replyComparison = buildInlineReviewReplyComparison({
        metadata,
        runFinalReply,
        runRawReply,
    });
    const commentIncludesReplyComparison = Boolean(
        annotation.comment?.includes('**Original reply**'),
    );
    const isUserAnnotation = annotation.source_type === 'user';
    const authorLabel =
        annotation.source_type === 'agent'
            ? 'Agent'
            : annotation.source_name || 'Unknown';
    const shouldShowUserLinks =
        isUserAnnotation && annotation.source_id && organizationId;
    const ratingLabel = metadata?.rating_label?.toLowerCase();
    const ratingBadge =
        ratingLabel === 'good' ? (
            <Badge className="bg-green-100 text-green-800">
                <ThumbsUp className="mr-1 h-3 w-3" />
                Good
            </Badge>
        ) : ratingLabel === 'bad' ? (
            <Badge className="bg-red-100 text-red-800">
                <ThumbsDown className="mr-1 h-3 w-3" />
                Bad
            </Badge>
        ) : null;

    return (
        <div className="rounded-lg border p-3">
            <div className="mb-2 space-y-1.5">
                <div className="flex min-w-0 items-start gap-2">
                    {annotation.source_type !== 'agent' ? (
                        <User className="text-muted-foreground h-4 w-4" />
                    ) : (
                        <Bot className="text-muted-foreground h-4 w-4" />
                    )}
                    <div className="min-w-0">
                        {!isUserAnnotation && (
                            <div className="text-muted-foreground text-xs">
                                {authorLabel}
                            </div>
                        )}
                        {shouldShowUserLinks ? (
                            <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-1 text-xs">
                                <Link
                                    to="/admin/users/$user-id"
                                    params={{
                                        'user-id': annotation.source_id ?? '',
                                    }}
                                    className="max-w-[180px] truncate hover:underline sm:max-w-[240px]"
                                >
                                    {annotation.source_name ?? 'User'}
                                </Link>
                                <span>·</span>
                                <Link
                                    to="/admin/organizations/$organization-id"
                                    params={{
                                        'organization-id': organizationId,
                                    }}
                                    className="max-w-[220px] truncate hover:underline sm:max-w-[320px]"
                                >
                                    {organizationName}
                                </Link>
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 pl-6">
                    {ratingBadge}
                    <span className="text-muted-foreground text-xs whitespace-nowrap">
                        {formatDistanceToNow(new Date(annotation.created_at), {
                            addSuffix: true,
                        })}
                    </span>
                </div>
            </div>
            {annotation.comment && (
                <div className="mb-2 text-sm leading-snug">
                    <ReactMarkdown components={compactMarkdownComponents}>
                        {annotation.comment}
                    </ReactMarkdown>
                </div>
            )}
            {replyComparison && !commentIncludesReplyComparison && (
                <InlineReviewReplyComparison comparison={replyComparison} />
            )}
            {metadata && (
                <div className="mb-2 space-y-1 text-sm">
                    {metadata.verdict && (
                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">
                                Verdict:
                            </span>
                            <span
                                className={cn(
                                    'rounded px-1.5 py-0.5 text-xs font-medium',
                                    metadata.verdict === 'BAD' &&
                                        'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300',
                                    metadata.verdict === 'GOOD' &&
                                        'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-300',
                                    metadata.verdict !== 'BAD' &&
                                        metadata.verdict !== 'GOOD' &&
                                        'bg-muted text-foreground/80',
                                )}
                            >
                                {metadata.verdict}
                            </span>
                        </div>
                    )}
                    {talentSignal && (
                        <TalentSignalDetails signal={talentSignal} />
                    )}
                    {metadata.score !== undefined && (
                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">
                                Score:
                            </span>
                            <span className="font-medium">
                                {metadata.score}
                            </span>
                        </div>
                    )}
                    {metadata.issues && metadata.issues.length > 0 && (
                        <div>
                            <span className="text-muted-foreground text-xs">
                                Issues:
                            </span>
                            <ul className="mt-1 list-inside list-disc text-xs">
                                {metadata.issues.map(
                                    (issue: string, idx: number) => (
                                        <li key={idx}>{issue}</li>
                                    ),
                                )}
                            </ul>
                        </div>
                    )}
                    {metadata.issue_buckets &&
                        metadata.issue_buckets.length > 0 && (
                            <div>
                                <span className="text-muted-foreground text-xs">
                                    Buckets:
                                </span>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {metadata.issue_buckets.map(
                                        (bucket, idx) => (
                                            <Badge
                                                key={`${bucket}-${idx}`}
                                                variant="secondary"
                                                className="text-xs"
                                            >
                                                {formatInlineReviewIssueBucket(
                                                    bucket,
                                                )}
                                            </Badge>
                                        ),
                                    )}
                                </div>
                            </div>
                        )}
                    {metadata.reasons && metadata.reasons.length > 0 && (
                        <div>
                            <span className="text-muted-foreground text-xs">
                                Reasons:
                            </span>
                            <ul className="mt-1 list-inside list-disc text-xs">
                                {metadata.reasons.map((reason, idx) => (
                                    <li key={idx}>{reason}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {metadata.model && (
                        <div className="flex items-center gap-2 text-xs">
                            <span className="text-muted-foreground">
                                Model:
                            </span>
                            <code className="bg-muted rounded px-1">
                                {metadata.model}
                            </code>
                        </div>
                    )}
                    {metadata.agent_version && (
                        <div className="flex items-center gap-2 text-xs">
                            <span className="text-muted-foreground">
                                Agent Version:
                            </span>
                            <code className="bg-muted rounded px-1">
                                {metadata.agent_version}
                            </code>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

type LLMItem = {
    readonly [key: string]: unknown;
    readonly id?: string;
    readonly type?: string;
    readonly text_content?: string | null;
    readonly tool_name?: string | null;
    readonly tool_status?: string | null;
    readonly arguments_json?: Record<string, unknown> | null;
    readonly output_json?: Record<string, unknown> | null;
    readonly direction?: string;
    readonly role?: string;
};

type AgencyEscalationDisplay = {
    readonly category: string | null;
    readonly summary: string | null;
    readonly urgency: string | null;
    readonly evidenceQuote: string | null;
};

const AGENCY_ESCALATION_CATEGORY_LABELS: Record<string, string> = {
    clock_issue: 'Clock issue',
    office_callback: 'Office callback',
    other: 'Other',
    payroll_or_hr: 'Payroll or HR',
    safety_or_care_issue: 'Safety or care issue',
    schedule_change: 'Schedule change',
};

const AGENCY_ESCALATION_URGENCY_LABELS: Record<string, string> = {
    high: 'High',
    normal: 'Normal',
    urgent: 'Urgent',
};

function stringifyTraceValue(value: unknown) {
    if (value === null || value === undefined) {
        return '(empty)';
    }

    if (typeof value === 'string') {
        return value || '(empty)';
    }

    return JSON.stringify(value, null, 2);
}

function formatTraceSection(title: string, content: string) {
    return `## ${title}\n${content}`;
}

function getActionString(
    action: Record<string, unknown>,
    key: string,
): string | null {
    const value = action[key];
    if (typeof value !== 'string') {
        return null;
    }

    const trimmedValue = value.trim();
    return trimmedValue || null;
}

function extractAgencyEscalationActions(
    actions: Array<Record<string, unknown>> | null | undefined,
): Array<AgencyEscalationDisplay> {
    return (actions ?? []).flatMap((action) => {
        if (action['type'] !== 'agency_escalation') {
            return [];
        }

        return [
            {
                category: getActionString(action, 'category'),
                summary: getActionString(action, 'summary'),
                urgency: getActionString(action, 'urgency'),
                evidenceQuote: getActionString(action, 'evidence_quote'),
            },
        ];
    });
}

function formatSnakeCaseFallbackLabel(value: string) {
    return value
        .split('_')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function formatAgencyEscalationCategory(value: string | null) {
    if (!value) {
        return null;
    }

    return (
        AGENCY_ESCALATION_CATEGORY_LABELS[value] ??
        formatSnakeCaseFallbackLabel(value)
    );
}

function formatAgencyEscalationUrgency(value: string | null) {
    if (!value) {
        return null;
    }

    return (
        AGENCY_ESCALATION_URGENCY_LABELS[value] ??
        formatSnakeCaseFallbackLabel(value)
    );
}

function formatAgencyEscalationsForCopy(
    escalations: Array<AgencyEscalationDisplay>,
) {
    return escalations
        .map((escalation, index) =>
            [
                `${index + 1}. What it was for: ${
                    escalation.summary ?? 'No summary recorded'
                }`,
                escalation.category
                    ? `Category: ${formatAgencyEscalationCategory(
                          escalation.category,
                      )}`
                    : null,
                escalation.urgency
                    ? `Urgency: ${formatAgencyEscalationUrgency(
                          escalation.urgency,
                      )}`
                    : null,
                escalation.evidenceQuote
                    ? `Evidence: ${escalation.evidenceQuote}`
                    : null,
            ]
                .filter(Boolean)
                .join('\n'),
        )
        .join('\n\n');
}

function formatLlmItemForCopy(item: LLMItem, index: number) {
    const label = [
        `Item ${index + 1}`,
        item.type ? `type=${item.type}` : null,
        item.tool_name ? `tool=${item.tool_name}` : null,
        item.role ? `role=${item.role}` : null,
        item.direction ? `direction=${item.direction}` : null,
    ]
        .filter(Boolean)
        .join(' | ');

    const parts = [
        label,
        item.text_content
            ? formatTraceSection('Text', item.text_content)
            : null,
        item.arguments_json
            ? formatTraceSection(
                  'Arguments JSON',
                  stringifyTraceValue(item.arguments_json),
              )
            : null,
        item.output_json
            ? formatTraceSection(
                  'Output JSON',
                  stringifyTraceValue(item.output_json),
              )
            : null,
        formatTraceSection('Raw item JSON', stringifyTraceValue(item)),
    ];

    return parts.filter((part) => part !== null).join('\n\n');
}

function formatRelativeTimestampForCopy(
    value: string | null | undefined,
    timezone = 'UTC',
) {
    if (!value) {
        return '—';
    }

    try {
        const date = new Date(value);
        return `${formatDistanceToNow(date, {
            addSuffix: true,
        })} (${formatInTimeZone(date, timezone, 'PPpp')})`;
    } catch {
        return value;
    }
}

function formatStatusForCopy(status: string | null | undefined) {
    if (!status) {
        return '—';
    }

    return status
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function formatStatusSetByForCopy(run: SmsEngineRunPageCopyInput) {
    if (run.status_source_type === 'agent') {
        return 'Agent';
    }

    if (run.status_set_by_user_name) {
        return run.status_set_by_user_name;
    }

    if (run.status_source_type === 'user') {
        return 'User Feedback';
    }

    if (run.status_source_type === 'human') {
        return 'Human';
    }

    return '—';
}

type SnapshotForCopy = {
    readonly caregiver?: {
        readonly first_name?: string | null;
        readonly last_name?: string | null;
        readonly preferred_name?: string | null;
        readonly language?: string | null;
    };
    readonly inbound?: {
        readonly body?: string | null;
        readonly received_at?: string | null;
    };
    readonly messages?: Array<{
        readonly role?: string | null;
        readonly body?: string | null;
        readonly source?: string | null;
        readonly timestamp?: string | null;
        readonly domain_links?: Array<Record<string, unknown>>;
    }>;
    readonly active_shifts?: Array<Record<string, unknown>>;
};

function asSnapshotForCopy(value: unknown): SnapshotForCopy {
    return value && typeof value === 'object' ? (value as SnapshotForCopy) : {};
}

function formatDomainLinkLabel(link: Record<string, unknown>) {
    if (link['type'] === 'shift_confirmation') {
        return 'Shift Confirmation';
    }
    if (link['type'] === 'callout_contact_attempt') {
        return 'Open Shift Offer';
    }
    return String(link['type'] ?? 'Domain Link');
}

function formatConversationForCopy(run: SmsEngineRunPageCopyInput) {
    const snapshot = asSnapshotForCopy(run.context_snapshot);
    const messages = snapshot.messages ?? [];
    const contextMessages =
        messages.length > 0
            ? messages
                  .map((message, index) => {
                      const speaker =
                          message.role === 'user' ? 'Caregiver' : 'Phoebe';
                      const source = message.source
                          ? ` (${message.source})`
                          : '';
                      const links = message.domain_links ?? [];
                      const linkLabels =
                          links.length > 0
                              ? `\nDomain links: ${links
                                    .map(formatDomainLinkLabel)
                                    .join(', ')}`
                              : '';
                      return [
                          `${index + 1}. ${speaker}${source}`,
                          message.timestamp
                              ? `Timestamp: ${message.timestamp}`
                              : null,
                          linkLabels || null,
                          message.body || '(empty)',
                      ]
                          .filter(Boolean)
                          .join('\n');
                  })
                  .join('\n\n')
            : 'No context messages available.';

    return [
        'Context (engine input)',
        contextMessages,
        'Inbound message (triggered this run)',
        snapshot.inbound?.body || '(empty)',
        'Generated reply',
        run.final_reply || '(empty)',
        [
            `Auto-send: ${run.should_auto_send ? 'Yes' : 'No'}`,
            `Skip Response: ${run.skip_response ? 'Yes' : 'No'}`,
            `Override Used: ${run.used_override ? 'Yes' : 'No'}`,
            `Off Hours: ${run.is_off_hours ? 'Yes' : 'No'}`,
            run.detected_language
                ? `Detected Language: ${run.detected_language}`
                : null,
        ]
            .filter(Boolean)
            .join('\n'),
        'Raw LLM Reply',
        run.raw_reply || '(empty)',
    ].join('\n\n');
}

function formatPendingItemsForCopy(run: SmsEngineRunPageCopyInput) {
    const snapshot = asSnapshotForCopy(run.context_snapshot);
    const domainLinks = (snapshot.messages ?? []).flatMap(
        (message) => message.domain_links ?? [],
    );
    const activeShifts = snapshot.active_shifts ?? [];

    const sections: Array<string> = [];
    if (domainLinks.length > 0) {
        sections.push(
            [
                'Domain Links',
                ...domainLinks.map((link, index) =>
                    [
                        `${index + 1}. ${formatDomainLinkLabel(link)}`,
                        stringifyTraceValue(link),
                    ].join('\n'),
                ),
            ].join('\n\n'),
        );
    }
    if (activeShifts.length > 0) {
        sections.push(
            [
                'Active Shifts',
                ...activeShifts.map((shift, index) =>
                    [`${index + 1}. Active shift`, stringifyTraceValue(shift)]
                        .filter(Boolean)
                        .join('\n'),
                ),
            ].join('\n\n'),
        );
    }

    return sections.length > 0 ? sections.join('\n\n') : 'No pending items.';
}

function formatActionsForCopy(
    actions: Array<Record<string, unknown>> | null | undefined,
) {
    if (!actions || actions.length === 0) {
        return 'No actions extracted.';
    }

    return actions
        .map((action, index) =>
            [
                `${index + 1}. ${String(action['type'] ?? 'Unknown')}`,
                action['response']
                    ? `Response: ${String(action['response'])}`
                    : null,
                action['reason'] ? `Reason: ${String(action['reason'])}` : null,
                action['urgency']
                    ? `Urgency: ${String(action['urgency'])}`
                    : null,
                action['summary']
                    ? `Summary: ${String(action['summary'])}`
                    : null,
                action['evidence_quote']
                    ? `Evidence: ${String(action['evidence_quote'])}`
                    : null,
                stringifyTraceValue(action),
            ]
                .filter(Boolean)
                .join('\n'),
        )
        .join('\n\n');
}

function formatTalentSignalForCopy(signal: TalentSignalDisplay) {
    const category = signal.category
        ? formatTalentSignalCategory(signal.category)
        : 'Detected';
    const confidence = signal.confidence
        ? ` (${formatTalentSignalConfidence(signal.confidence)} confidence)`
        : '';

    return [
        `Talent signal: ${category}${confidence}`,
        signal.reason ? `Reason: ${signal.reason}` : null,
        signal.evidenceQuote ? `Evidence: ${signal.evidenceQuote}` : null,
    ]
        .filter(Boolean)
        .join('\n');
}

function formatAnnotationsForCopy(
    annotations: Array<SmsEngineRunPageCopyAnnotation> | null | undefined,
    timezone: string,
) {
    if (!annotations || annotations.length === 0) {
        return 'No comments on this run.';
    }

    return annotations
        .map((annotation, index) => {
            const metadata =
                annotation.annotation_metadata as AnnotationMetadata | null;
            const talentSignal = getTalentSignalDisplay(metadata);
            const metadataLines = [
                metadata?.verdict ? `Verdict: ${metadata.verdict}` : null,
                talentSignal ? formatTalentSignalForCopy(talentSignal) : null,
                metadata?.issues?.length
                    ? `Issues:\n${metadata.issues
                          .map((issue) => `- ${issue}`)
                          .join('\n')}`
                    : null,
                metadata?.issue_buckets?.length
                    ? `Buckets: ${metadata.issue_buckets
                          .map((bucket) =>
                              formatInlineReviewIssueBucket(bucket),
                          )
                          .join(', ')}`
                    : null,
                metadata?.reasons?.length
                    ? `Reasons:\n${metadata.reasons
                          .map((reason) => `- ${reason}`)
                          .join('\n')}`
                    : null,
                metadata?.score !== undefined
                    ? `Score: ${metadata.score}`
                    : null,
                metadata?.model ? `Model: ${metadata.model}` : null,
                metadata?.agent_version
                    ? `Agent Version: ${metadata.agent_version}`
                    : null,
                metadata
                    ? `Metadata JSON:\n${stringifyTraceValue(metadata)}`
                    : null,
            ]
                .filter(Boolean)
                .join('\n');

            return [
                `${index + 1}. ${annotation.source_name ?? annotation.source_type ?? 'Unknown'}`,
                `Created at: ${formatRelativeTimestampForCopy(
                    annotation.created_at,
                    timezone,
                )}`,
                annotation.comment || '(no comment)',
                metadataLines || null,
            ]
                .filter(Boolean)
                .join('\n\n');
        })
        .join('\n\n---\n\n');
}

function formatReplayForCopy(replayResult: ReplayResult | null | undefined) {
    if (!replayResult) {
        return 'No replay result loaded.';
    }

    const llmItems =
        replayResult.llm_items.length > 0
            ? replayResult.llm_items
                  .map((item, index) => formatLlmItemForCopy(item, index))
                  .join('\n\n---\n\n')
            : 'No replay LLM items recorded.';

    return [
        `Model: ${replayResult.model}`,
        `Auto-send: ${replayResult.should_auto_send ? 'Yes' : 'No'}`,
        `Skip Response: ${replayResult.skip_response ? 'Yes' : 'No'}`,
        `Detected Language: ${replayResult.detected_language ?? 'None'}`,
        formatTraceSection(
            'Final Reply',
            replayResult.final_reply || '(empty)',
        ),
        formatTraceSection('Raw Reply', replayResult.raw_reply || '(empty)'),
        formatTraceSection(
            'Actions JSON',
            stringifyTraceValue(replayResult.actions),
        ),
        formatTraceSection('LLM Execution Items', llmItems),
    ].join('\n\n');
}

export function buildSmsEngineRunPageCopyText({
    run,
    replayResult,
}: {
    run: SmsEngineRunPageCopyInput;
    replayResult?: ReplayResult | null;
}) {
    const snapshot = asSnapshotForCopy(run.context_snapshot);
    const timezone = run.organization_timezone ?? 'UTC';
    const snapshotCaregiverName = [
        snapshot.caregiver?.first_name,
        snapshot.caregiver?.last_name,
    ]
        .filter(Boolean)
        .join(' ');
    const caregiverName =
        ((run.caregiver_name ??
            snapshot.caregiver?.preferred_name ??
            snapshotCaregiverName) ||
            run.caregiver_id) ??
        'Unknown';
    const metadata = [
        `Run ID: ${run.id}`,
        `Created at: ${formatRelativeTimestampForCopy(
            run.created_at,
            timezone,
        )}`,
        `Organization: ${run.organization_name ?? 'Unknown'}`,
        `Timezone: ${run.organization_timezone ?? 'Unknown'}`,
        `Status: ${formatStatusForCopy(run.status)}`,
        `Status set by: ${formatStatusSetByForCopy(run)}`,
        `Status set at: ${formatRelativeTimestampForCopy(
            run.status_set_at,
            timezone,
        )}`,
        `Conversation ID: ${run.conversation_id}`,
        `Message ID: ${run.message_id}`,
        `Braintrust Run: ${
            run.braintrust_run_url ?? run.braintrust_run_id ?? '—'
        }`,
        `Caregiver: ${caregiverName || 'Unknown'}`,
        `Model: ${run.model ?? 'Unknown'}`,
        `Domain: ${run.domain_type ?? 'Unknown'}`,
        `Domain ID: ${run.domain_id ?? 'None'}`,
        `Should auto send: ${String(run.should_auto_send ?? false)}`,
        `Skip response: ${String(run.skip_response ?? false)}`,
        `Used override: ${String(run.used_override ?? false)}`,
        `Off hours: ${String(run.is_off_hours ?? false)}`,
        `Detected language: ${run.detected_language ?? 'None'}`,
    ].join('\n');

    const llmItems = run.llm_items ?? [];
    const formattedItems =
        llmItems.length > 0
            ? llmItems
                  .map((item, index) => formatLlmItemForCopy(item, index))
                  .join('\n\n---\n\n')
            : 'No LLM items recorded.';
    const agencyEscalations = extractAgencyEscalationActions(run.actions);

    return [
        '# SMS Engine Run Page',
        metadata,
        formatTraceSection(
            'Context',
            stringifyTraceValue(run.context_snapshot),
        ),
        formatTraceSection('Conversation', formatConversationForCopy(run)),
        formatTraceSection('Replay', formatReplayForCopy(replayResult)),
        formatTraceSection(
            'Pending Items (what engine saw)',
            formatPendingItemsForCopy(run),
        ),
        formatTraceSection('LLM Execution', formattedItems),
        formatTraceSection(
            'Extracted Actions',
            formatActionsForCopy(run.actions),
        ),
        ...(agencyEscalations.length > 0
            ? [
                  formatTraceSection(
                      'Agency Escalation',
                      formatAgencyEscalationsForCopy(agencyEscalations),
                  ),
              ]
            : []),
        formatTraceSection(
            'Comments',
            formatAnnotationsForCopy(run.annotations, timezone),
        ),
    ].join('\n\n');
}

function LLMItemStep({ item }: { item: LLMItem }) {
    const [isOpen, setIsOpen] = useState(false);

    const isToolCall =
        item.type === 'function_call' || item.type === 'function_output';
    const isReasoning = item.type === 'reasoning';
    const isText = item.type === 'text';

    if (isReasoning) {
        return (
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <div className="flex items-start gap-2 py-1">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <div className="min-w-0 flex-1">
                        <CollapsibleTrigger className="flex items-center gap-1.5 text-left">
                            <span className="text-muted-foreground text-sm">
                                Thought
                            </span>
                            {isOpen ? (
                                <ChevronDown className="text-muted-foreground h-3.5 w-3.5" />
                            ) : (
                                <ChevronRight className="text-muted-foreground h-3.5 w-3.5" />
                            )}
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div className="bg-muted mt-1 rounded p-2 text-xs">
                                {item.text_content || '(empty)'}
                            </div>
                        </CollapsibleContent>
                    </div>
                </div>
            </Collapsible>
        );
    }

    if (isToolCall) {
        const isOutput = item.type === 'function_output';
        return (
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <div className="flex items-start gap-2 py-1">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <div className="min-w-0 flex-1">
                        <CollapsibleTrigger className="flex items-center gap-1.5 text-left">
                            {isOpen ? (
                                <ChevronDown className="text-muted-foreground h-3.5 w-3.5" />
                            ) : (
                                <ChevronRight className="text-muted-foreground h-3.5 w-3.5" />
                            )}
                            <code className="text-sm font-medium">
                                {item.tool_name || 'Unknown tool'}
                            </code>
                            {isOutput ? (
                                <Check className="h-3 w-3 text-green-500" />
                            ) : (
                                <Zap className="h-3 w-3 text-blue-500" />
                            )}
                            <span className="text-muted-foreground text-xs">
                                {isOutput ? 'result' : 'call'}
                            </span>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div className="mt-1 space-y-1 pl-5">
                                {item.arguments_json && (
                                    <div>
                                        <span className="text-muted-foreground text-xs">
                                            Arguments:
                                        </span>
                                        <pre className="bg-muted mt-0.5 overflow-x-auto rounded p-2 text-xs">
                                            {JSON.stringify(
                                                item.arguments_json,
                                                null,
                                                2,
                                            )}
                                        </pre>
                                    </div>
                                )}
                                {item.output_json && (
                                    <div>
                                        <span className="text-muted-foreground text-xs">
                                            Result:
                                        </span>
                                        <pre className="bg-muted mt-0.5 overflow-x-auto rounded p-2 text-xs">
                                            {JSON.stringify(
                                                item.output_json,
                                                null,
                                                2,
                                            )}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </CollapsibleContent>
                    </div>
                </div>
            </Collapsible>
        );
    }

    if (isText && item.text_content) {
        const contextIdx = item.text_content.indexOf('\n\nContext:\n');
        let preamble: string | null = null;
        let prettyJson: string | null = null;
        if (contextIdx !== -1) {
            preamble = item.text_content.slice(0, contextIdx);
            const jsonStr = item.text_content.slice(
                contextIdx + '\n\nContext:\n'.length,
            );
            try {
                const parsed: unknown = JSON.parse(jsonStr);
                prettyJson = JSON.stringify(parsed, null, 2);
            } catch {
                // not valid JSON, keep as-is
            }
        }

        return (
            <div className="flex items-start gap-2 py-1">
                <span className="text-muted-foreground mt-0.5">•</span>
                <div className="min-w-0 flex-1">
                    <span className="text-muted-foreground text-sm">
                        Text Response:
                    </span>
                    <div className="mt-1 rounded bg-blue-50 p-2 text-sm break-words whitespace-pre-wrap dark:bg-blue-950/50">
                        {preamble ?? item.text_content}
                    </div>
                    {prettyJson && (
                        <Collapsible defaultOpen={true}>
                            <CollapsibleTrigger className="text-muted-foreground mt-1 flex items-center gap-1 text-xs">
                                <ChevronRight className="h-3.5 w-3.5" />
                                Context Snapshot
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <pre className="bg-muted mt-1 max-h-96 overflow-auto rounded p-2 text-xs">
                                    {prettyJson}
                                </pre>
                            </CollapsibleContent>
                        </Collapsible>
                    )}
                </div>
            </div>
        );
    }

    return null;
}

function ActionLabel({ action }: { action: Record<string, unknown> }) {
    const type = action['type'] as string | undefined;
    const response = action['response'] as string | undefined;
    const reason = action['reason'] as string | undefined;
    const status = action['status'] as string | undefined;
    const scope = action['scope'] as string | undefined;
    const dayOfWeek = action['day_of_week'] as string | undefined;
    const startTime = action['start_time'] as string | undefined;
    const endTime = action['end_time'] as string | undefined;
    const note = action['note'] as string | undefined;

    if (type === 'shift_confirmation_response') {
        return <Fragment>ShiftConfirmationResponse: {response}</Fragment>;
    }
    if (type === 'callout_response') {
        return <Fragment>OpenShiftResponse: {response}</Fragment>;
    }
    if (type === 'clock_in') {
        return <Fragment>ClockIn</Fragment>;
    }
    if (type === 'clock_out') {
        return <Fragment>ClockOut</Fragment>;
    }
    if (type === 'shift_cancellation') {
        return <Fragment>ShiftCancellation</Fragment>;
    }
    if (type === 'shift_cancellation_clarification') {
        return <Fragment>ShiftCancellationClarification</Fragment>;
    }
    if (type === 'shift_cancellation_confirmation_request') {
        return <Fragment>ShiftCancellationConfirmationRequest</Fragment>;
    }
    if (type === 'no_response_needed') {
        return <Fragment>NoResponseNeeded: {reason}</Fragment>;
    }
    if (type === 'caregiver_availability') {
        return (
            <Fragment>
                CaregiverAvailability: {status ?? 'unknown'}{' '}
                {scope ? `(${scope})` : ''}
                {dayOfWeek ? ` ${dayOfWeek}` : ''}{' '}
                {startTime && endTime ? `${startTime}-${endTime}` : ''}
            </Fragment>
        );
    }
    if (type === 'caregiver_memory') {
        return <Fragment>CaregiverMemory: {note ?? 'saved'}</Fragment>;
    }
    if (type === 'agency_escalation') {
        return <Fragment>AgencyEscalation</Fragment>;
    }
    return <Fragment>{type ?? 'Unknown'}</Fragment>;
}

function AgencyEscalationList({
    escalations,
}: {
    escalations: Array<AgencyEscalationDisplay>;
}) {
    return (
        <div className="space-y-3">
            {escalations.map((escalation, index) => (
                <div
                    key={index}
                    className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/50"
                >
                    <div className="flex flex-wrap gap-2">
                        {escalation.category && (
                            <Badge variant="outline" className="bg-white">
                                {formatAgencyEscalationCategory(
                                    escalation.category,
                                )}
                            </Badge>
                        )}
                        {escalation.urgency && (
                            <Badge variant="outline" className="bg-white">
                                {formatAgencyEscalationUrgency(
                                    escalation.urgency,
                                )}
                            </Badge>
                        )}
                    </div>
                    <div className="space-y-1">
                        <p className="text-muted-foreground text-xs">
                            What it was for
                        </p>
                        <p className="text-sm break-words whitespace-pre-wrap">
                            {escalation.summary ?? 'No summary recorded'}
                        </p>
                    </div>
                    {escalation.evidenceQuote && (
                        <div className="space-y-1">
                            <p className="text-muted-foreground text-xs">
                                Evidence
                            </p>
                            <p className="text-sm break-words whitespace-pre-wrap">
                                {escalation.evidenceQuote}
                            </p>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}
